var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import i18next from 'i18next';
import { v4 as uuidV4 } from 'uuid';
import fixWebmDuration from 'webm-duration-fix';
import { getRoomName } from '../../../base/conference/functions';
import { MEDIA_TYPE } from '../../../base/media/constants';
import { getLocalTrack, getTrackState } from '../../../base/tracks/functions';
import { inIframe } from '../../../base/util/iframeUtils';
import { stopLocalVideoRecording } from '../../actions.any';
const getMimeType = () => {
    const possibleTypes = [
        'video/mp4;codecs=h264',
        'video/webm;codecs=h264',
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8'
    ];
    for (const type of possibleTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
            return type;
        }
    }
    throw new Error('No MIME Type supported by MediaRecorder');
};
const VIDEO_BIT_RATE = 2500000; // 2.5Mbps in bits
// Lazily initialize.
let preferredMediaType;
const LocalRecordingManager = {
    recordingData: [],
    recorder: undefined,
    stream: undefined,
    audioContext: undefined,
    audioDestination: undefined,
    roomName: '',
    totalSize: 1073741824,
    selfRecording: {
        on: false,
        withVideo: false
    },
    get mediaType() {
        if (!preferredMediaType) {
            preferredMediaType = getMimeType();
        }
        return preferredMediaType;
    },
    /**
     * Initializes audio context used for mixing audio tracks.
     *
     * @returns {void}
     */
    initializeAudioMixer() {
        this.audioContext = new AudioContext();
        this.audioDestination = this.audioContext.createMediaStreamDestination();
    },
    /**
     * Mixes multiple audio tracks to the destination media stream.
     *
     * @param {MediaStream} stream - The stream to mix.
     * @returns {void}
     * */
    mixAudioStream(stream) {
        var _a;
        if (stream.getAudioTracks().length > 0 && this.audioDestination) {
            (_a = this.audioContext) === null || _a === void 0 ? void 0 : _a.createMediaStreamSource(stream).connect(this.audioDestination);
        }
    },
    /**
     * Adds audio track to the recording stream.
     *
     * @param {MediaStreamTrack} track - The track to be added.
     * @returns {void}
     */
    addAudioTrackToLocalRecording(track) {
        if (this.selfRecording.on) {
            return;
        }
        if (track) {
            const stream = new MediaStream([track]);
            this.mixAudioStream(stream);
        }
    },
    /**
     * Returns a filename based ono the Jitsi room name in the URL and timestamp.
     *
     * @returns {string}
     * */
    getFilename() {
        const now = new Date();
        const timestamp = now.toISOString();
        return `${this.roomName}_${timestamp}`;
    },
    /**
     * Saves local recording to file.
     *
     * @param {Array} recordingData - The recording data.
     * @param {string} filename - The name of the file.
     * @returns {void}
     * */
    saveRecording(recordingData, filename) {
        return __awaiter(this, void 0, void 0, function* () {
            // @ts-ignore
            const blob = yield fixWebmDuration(new Blob(recordingData, { type: this.mediaType }));
            // @ts-ignore
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            const extension = this.mediaType.slice(this.mediaType.indexOf('/') + 1, this.mediaType.indexOf(';'));
            a.style.display = 'none';
            a.href = url;
            a.download = `${filename}.${extension}`;
            a.click();
        });
    },
    /**
     * Stops local recording.
     *
     * @returns {void}
     * */
    stopLocalRecording() {
        if (this.recorder) {
            this.recorder.stop();
            this.recorder = undefined;
            this.audioContext = undefined;
            this.audioDestination = undefined;
            setTimeout(() => this.saveRecording(this.recordingData, this.getFilename()), 1000);
        }
    },
    /**
     * Starts a local recording.
     *
     * @param {IStore} store - The redux store.
     * @param {boolean} onlySelf - Whether to record only self streams.
     * @returns {void}
     */
    startLocalRecording(store, onlySelf) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        return __awaiter(this, void 0, void 0, function* () {
            const { dispatch, getState } = store;
            // @ts-ignore
            const supportsCaptureHandle = Boolean(navigator.mediaDevices.setCaptureHandleConfig) && !inIframe();
            const tabId = uuidV4();
            this.selfRecording.on = onlySelf;
            this.recordingData = [];
            this.roomName = (_a = getRoomName(getState())) !== null && _a !== void 0 ? _a : '';
            let gdmStream = new MediaStream();
            const tracks = getTrackState(getState());
            if (onlySelf) {
                let audioTrack = (_c = (_b = getLocalTrack(tracks, MEDIA_TYPE.AUDIO)) === null || _b === void 0 ? void 0 : _b.jitsiTrack) === null || _c === void 0 ? void 0 : _c.track;
                let videoTrack = (_e = (_d = getLocalTrack(tracks, MEDIA_TYPE.VIDEO)) === null || _d === void 0 ? void 0 : _d.jitsiTrack) === null || _e === void 0 ? void 0 : _e.track;
                if (!audioTrack) {
                    APP.conference.muteAudio(false);
                    setTimeout(() => APP.conference.muteAudio(true), 100);
                    yield new Promise(resolve => {
                        setTimeout(resolve, 100);
                    });
                }
                if (videoTrack && videoTrack.readyState !== 'live') {
                    videoTrack = undefined;
                }
                audioTrack = (_g = (_f = getLocalTrack(getTrackState(getState()), MEDIA_TYPE.AUDIO)) === null || _f === void 0 ? void 0 : _f.jitsiTrack) === null || _g === void 0 ? void 0 : _g.track;
                if (!audioTrack && !videoTrack) {
                    throw new Error('NoLocalStreams');
                }
                this.selfRecording.withVideo = Boolean(videoTrack);
                const localTracks = [];
                audioTrack && localTracks.push(audioTrack);
                videoTrack && localTracks.push(videoTrack);
                this.stream = new MediaStream(localTracks);
            }
            else {
                if (supportsCaptureHandle) {
                    // @ts-ignore
                    navigator.mediaDevices.setCaptureHandleConfig({
                        handle: `JitsiMeet-${tabId}`,
                        permittedOrigins: ['*']
                    });
                }
                const localAudioTrack = (_j = (_h = getLocalTrack(tracks, MEDIA_TYPE.AUDIO)) === null || _h === void 0 ? void 0 : _h.jitsiTrack) === null || _j === void 0 ? void 0 : _j.track;
                // Starting chrome 107, the recorder does not record any data if the audio stream has no tracks
                // To fix this we create a track for the local user(muted track)
                if (!localAudioTrack) {
                    APP.conference.muteAudio(false);
                    setTimeout(() => APP.conference.muteAudio(true), 100);
                    yield new Promise(resolve => {
                        setTimeout(resolve, 100);
                    });
                }
                const currentTitle = document.title;
                document.title = i18next.t('localRecording.selectTabTitle');
                // @ts-ignore
                gdmStream = yield navigator.mediaDevices.getDisplayMedia({
                    // @ts-ignore
                    video: { displaySurface: 'browser',
                        frameRate: 30 },
                    audio: false,
                    preferCurrentTab: true
                });
                document.title = currentTitle;
                // @ts-ignore
                const isBrowser = gdmStream.getVideoTracks()[0].getSettings().displaySurface === 'browser';
                if (!isBrowser || (supportsCaptureHandle // @ts-ignore
                    && ((_k = gdmStream.getVideoTracks()[0].getCaptureHandle()) === null || _k === void 0 ? void 0 : _k.handle) !== `JitsiMeet-${tabId}`)) {
                    gdmStream.getTracks().forEach((track) => track.stop());
                    throw new Error('WrongSurfaceSelected');
                }
                this.initializeAudioMixer();
                const allTracks = getTrackState(getState());
                allTracks.forEach((track) => {
                    var _a;
                    if (track.mediaType === MEDIA_TYPE.AUDIO) {
                        const audioTrack = (_a = track === null || track === void 0 ? void 0 : track.jitsiTrack) === null || _a === void 0 ? void 0 : _a.track;
                        this.addAudioTrackToLocalRecording(audioTrack);
                    }
                });
                this.stream = new MediaStream([
                    ...((_l = this.audioDestination) === null || _l === void 0 ? void 0 : _l.stream.getAudioTracks()) || [],
                    gdmStream.getVideoTracks()[0]
                ]);
            }
            this.recorder = new MediaRecorder(this.stream, {
                mimeType: this.mediaType,
                videoBitsPerSecond: VIDEO_BIT_RATE
            });
            this.recorder.addEventListener('dataavailable', e => {
                if (e.data && e.data.size > 0) {
                    this.recordingData.push(e.data);
                    this.totalSize -= e.data.size;
                    if (this.totalSize <= 0) {
                        this.stopLocalRecording();
                    }
                }
            });
            if (!onlySelf) {
                this.recorder.addEventListener('stop', () => {
                    var _a;
                    (_a = this.stream) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((track) => track.stop());
                    gdmStream === null || gdmStream === void 0 ? void 0 : gdmStream.getTracks().forEach((track) => track.stop());
                });
                gdmStream === null || gdmStream === void 0 ? void 0 : gdmStream.addEventListener('inactive', () => {
                    dispatch(stopLocalVideoRecording());
                });
                this.stream.addEventListener('inactive', () => {
                    dispatch(stopLocalVideoRecording());
                });
            }
            this.recorder.start(5000);
        });
    },
    /**
     * Whether or not we're currently recording locally.
     *
     * @returns {boolean}
     */
    isRecordingLocally() {
        return Boolean(this.recorder);
    }
};
export default LocalRecordingManager;
